import React, { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, Shield, ArrowLeft, Clock, Eye, EyeOff } from 'lucide-react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '../../hooks/useAuth';
import { useLoginCredentials, useVerifyOtp } from '../../hooks/useApi';
import { apiClient } from '../../services/api';
import { ENV } from '../../config/env';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button, Input, Label } from '@/components/ui/shadcn';
import { LoadingSpinner } from '../ui/Loading';

interface CredentialsFormData {
  usernameOrEmail: string;
  password: string;
}

interface OtpFormData {
  otp: string;
}

const LoginPage: React.FC = () => {
  const { isAuthenticated, login: setAuthLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'credentials' | 'code'>('credentials');
  const [pendingToken, setPendingToken] = useState<string>('');
  const [usernameOrEmailDisplay, setUsernameOrEmailDisplay] = useState('');
  const [countdown, setCountdown] = useState(0);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRequired = ENV.TURNSTILE_ENABLED && !!ENV.TURNSTILE_SITE_KEY;

  const credentialsForm = useForm<CredentialsFormData>({
    mode: 'onSubmit',
    defaultValues: { usernameOrEmail: '', password: '' },
  });
  const otpForm = useForm<OtpFormData>({
    mode: 'onSubmit',
    defaultValues: { otp: '' },
  });

  const loginCredentialsMutation = useLoginCredentials();
  const verifyOtpMutation = useVerifyOtp();

  const fromLocation = location.state?.from;
  const from = (fromLocation && fromLocation.pathname && fromLocation.pathname.startsWith('/'))
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}` 
    : '/dashboard';

  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const finishLogin = async (admin: any, token: string) => {
    setAuthLogin(admin, token);
    try {
      const currentAdminResponse = await apiClient.getCurrentAdmin();
      if (currentAdminResponse.data?.admin && currentAdminResponse.data?.permissions) {
        const updatedAdmin = {
          ...currentAdminResponse.data.admin,
          permissionObjects: currentAdminResponse.data.permissions,
          permissions: currentAdminResponse.data.permissions.map((p: any) => p.code),
        };
        setAuthLogin(updatedAdmin, token);
      }
    } catch (permError) {
      console.error('Error fetching permissions after login:', permError);
    }
    navigate(from, { replace: true });
  };

  const onCredentialsSubmit = async (data: CredentialsFormData) => {
    if (captchaRequired && !captchaToken) {
      toast.error('Please complete the CAPTCHA');
      return;
    }
    try {
      const result = await loginCredentialsMutation.mutateAsync({
        usernameOrEmail: data.usernameOrEmail.trim(),
        password: data.password,
        ...(captchaToken ? { captchaToken } : {}),
      });
      const resData = result.data;
      if (!resData) return;

      if ('token' in resData && 'admin' in resData) {
        toast.success(result.message || 'Login successful.');
        await finishLogin(resData.admin, resData.token);
        return;
      }
      if ('needsOtp' in resData && resData.needsOtp && 'pendingToken' in resData) {
        setPendingToken(resData.pendingToken);
        setUsernameOrEmailDisplay(data.usernameOrEmail);
        setStep('code');
        setCountdown(60);
        otpForm.setValue('otp', '');
        toast.success(result.message || 'Please enter the OTP sent to your email.');
      }
    } catch {
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  const onOtpSubmit = async (data: OtpFormData) => {
    if (!pendingToken) return;
    try {
      const result = await verifyOtpMutation.mutateAsync({
        pendingToken,
        otp: data.otp,
      });
      if (result.data?.token && result.data?.admin) {
        toast.success(result.message || 'Login successful.');
        await finishLogin(result.data.admin, result.data.token);
      }
    } catch (error) {
      otpForm.setValue('otp', '');
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setPendingToken('');
    setCountdown(0);
    otpForm.setValue('otp', '');
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    handleBackToCredentials();
  };

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Admin Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Secure access to your dashboard
          </p>
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              Admin Login
            </CardTitle>
            <CardDescription className="text-center">
              {step === 'credentials'
                ? 'Enter your email or username and password'
                : 'Enter the OTP sent to your email'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'credentials' ? (
              <form onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)} className="space-y-6">
                <div>
                  <Label htmlFor="usernameOrEmail" className="block text-gray-700">
                    Email or Username
                  </Label>
                  <div className="mt-1 relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="usernameOrEmail"
                      type="text"
                      autoComplete="username"
                      placeholder="Enter your email or username"
                      className="pl-10"
                      {...credentialsForm.register('usernameOrEmail', {
                        required: 'Email or username is required',
                      })}
                    />
                  </div>
                  {credentialsForm.formState.errors.usernameOrEmail && (
                    <p className="mt-1 text-sm text-red-600">
                      {credentialsForm.formState.errors.usernameOrEmail.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password" className="block text-gray-700">
                    Password
                  </Label>
                  <div className="mt-1 relative">
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      {...credentialsForm.register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters',
                        },
                      })}
                    />
                  </div>
                  {credentialsForm.formState.errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {credentialsForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                {captchaRequired && (
                  <div className="flex justify-center min-h-[65px]">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={ENV.TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => {
                        setCaptchaToken(null);
                        turnstileRef.current?.reset();
                      }}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loginCredentialsMutation.isLoading}
                >
                  {loginCredentialsMutation.isLoading ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner />
                      <span className="ml-2">Signing In...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <p className="text-center text-sm">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-6">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">
                    Check Your Email
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    We sent a verification code for <strong>{usernameOrEmailDisplay}</strong>
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Code expires in 5 minutes
                  </p>
                </div>

                <div>
                  <Label htmlFor="otp" className="block text-gray-700">
                    OTP Code
                  </Label>
                  <div className="mt-1 relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      className="pl-10 pr-10 text-center text-lg tracking-widest"
                      maxLength={6}
                      {...otpForm.register('otp', {
                        required: 'OTP is required',
                        minLength: { value: 6, message: 'Code must be 6 digits' },
                        pattern: { value: /^\d{6}$/, message: 'Code must be 6 digits' },
                      })}
                    />
                  </div>
                  {otpForm.formState.errors.otp && (
                    <p className="mt-1 text-sm text-red-600">
                      {otpForm.formState.errors.otp.message}
                    </p>
                  )}
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleBackToCredentials}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={verifyOtpMutation.isLoading}
                  >
                    {verifyOtpMutation.isLoading ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner />
                        <span className="ml-2">Signing In...</span>
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Didn&apos;t receive the code?{' '}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={countdown > 0}
                      className="text-blue-600 hover:text-blue-500 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {countdown > 0 ? (
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          Resend in {countdown}s
                        </span>
                      ) : (
                        'Back to sign in again'
                      )}
                    </button>
                  </p>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Secure admin access • Protected by 2FA when enabled
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
